import { useState } from "react";
import { SessionSummaryPane } from "./SessionSummaryPane";
import { SessionActivityStream } from "./SessionActivityStream";
import { useSessionEvents } from "@/hooks/useSessionEvents";
import { useRunProgress } from "@/hooks/useRunProgress";
import { useDecisions } from "@/hooks/queries/use-decisions";
import { cn } from "@/lib/utils";
import { parseAlternatives } from "@/lib/decisions-utils";
import { SESSION_MONITOR_POLL_MS } from "@/lib/polling";
import { Loader2, AlertCircle, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OverlayPanel } from "@/components/ui/overlay-panel";
import type { Decision } from "@/lib/api/decisions";

/**
 * Props for SessionMonitor component
 */
export interface SessionMonitorProps {
  /** Epic ID to monitor */
  epicId: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SessionMonitor container component
 * 
 * Combines SessionSummaryPane and SessionActivityStream to provide
 * a complete view of real-time session progress for an epic.
 */
export function SessionMonitor({ epicId, className }: SessionMonitorProps) {
  const [decisionsOpen, setDecisionsOpen] = useState(false);

  // Events feed the activity stream only. Every count, the current stage and
  // the progress bar come from the build's RunProgress (ENG-370), the same
  // source the job page uses, so the two never disagree.
  const { events, isLoading, error, refetch } = useSessionEvents({
    epicId,
    enabled: true,
    // Closer watch than the activity feed (this view exists to follow a run),
    // but not the former 2s — see src/lib/polling.ts.
    refetchInterval: SESSION_MONITOR_POLL_MS,
  });

  const { progress } = useRunProgress("build", epicId);

  const { data: decisionsData } = useDecisions({ epicId });

  const decisions: Decision[] = decisionsData?.data ?? [];

  // Show loading state during initial fetch
  if (isLoading && events.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-full p-8", className)}>
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading session data...</p>
        </div>
      </div>
    );
  }

  // Show error state only if no events have been received yet
  if (error && events.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-full p-8", className)}>
        <div className="flex flex-col items-center gap-3 text-destructive max-w-md">
          <AlertCircle className="h-8 w-8" />
          <p className="text-sm font-medium">Unable to load session data</p>
          <p className="text-xs text-muted-foreground text-center">
            {error.message}
          </p>
          <Button variant="outline" size="sm" onClick={refetch} className="mt-2">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Show "no active session" message if no events received
  if (events.length === 0 && !isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-full p-8", className)}>
        <div className="flex flex-col items-center gap-3 text-muted-foreground max-w-md text-center">
          <AlertCircle className="h-8 w-8" />
          <p className="text-sm font-medium">No active session</p>
          <p className="text-xs">
            Start an orchestrator run to see real-time progress monitoring here.
          </p>
        </div>
      </div>
    );
  }

  // Render the monitor with summary pane and activity stream
  return (
    <div className={cn("flex flex-col gap-5 w-full h-full min-h-0 overflow-hidden", className)}>
      {/* Stats bar — full width horizontal */}
      <SessionSummaryPane
        progress={progress}
        decisionsCount={decisions.length}
        onDecisionsClick={() => setDecisionsOpen(true)}
      />
      {/* Activity stream — full width */}
      <SessionActivityStream events={events} />

      {/* Decisions overlay panel */}
      <OverlayPanel
        open={decisionsOpen}
        onClose={() => setDecisionsOpen(false)}
        title="Decisions"
        className="w-[min(560px,calc(100vw-2rem))]"
        badge={
          <span className="text-xs text-muted-foreground bg-white/[0.06] rounded-full px-2 py-0.5 tabular-nums">
            {decisions.length}
          </span>
        }
        titleClassName="text-orange-400"
      >
        {/* Toolbar */}
        {decisions.length > 0 && (
          <div className="flex items-center justify-end px-5 py-2 border-b border-white/[0.06]">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
              onClick={() => exportDecisionsAsMarkdown(decisions)}
            >
              <Download className="h-3.5 w-3.5" />
              Export as Markdown
            </Button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          <DecisionsSheetBody decisions={decisions} />
        </div>
      </OverlayPanel>
    </div>
  );
}

// ─── Decisions sheet body ──────────────────────────────────────────

const CATEGORY_META: Record<string, { emoji: string; color: string }> = {
  architecture: { emoji: "🏗️", color: "text-blue-400" },
  library: { emoji: "📦", color: "text-green-400" },
  approach: { emoji: "🔧", color: "text-orange-400" },
  scope: { emoji: "📐", color: "text-violet-400" },
  design: { emoji: "🎨", color: "text-rose-400" },
  tradeoff: { emoji: "⚖️", color: "text-red-400" },
  deferral: { emoji: "⏳", color: "text-zinc-400" },
};

const IMPACT_STYLE: Record<string, string> = {
  high: "bg-red-500/10 text-red-400 border-red-500/20",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
};

// ─── Markdown export ───────────────────────────────────────────────

function exportDecisionsAsMarkdown(decisions: Decision[]): void {
  const lines: string[] = [
    "# Decision Log\n",
    `> Exported on ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}\n`,
  ];

  for (const d of decisions) {
    const cat = CATEGORY_META[d.category] ?? { emoji: "❓" };

    lines.push(`## ${cat.emoji} ${d.question}\n`);
    lines.push(`- **Category:** ${d.category}`);
    if (d.impact) lines.push(`- **Impact:** ${d.impact}`);
    if (d.madeBy) lines.push(`- **Made by:** ${d.madeBy}`);
    if (d.createdAt) {
      const parsed = new Date(d.createdAt);
      if (!isNaN(parsed.getTime())) {
        lines.push(
          `- **Date:** ${parsed.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
        );
      }
    }
    lines.push("");

    lines.push("### Decision\n");
    lines.push(`${d.decision}\n`);

    if (d.rationale) {
      lines.push("### Rationale\n");
      lines.push(`${d.rationale}\n`);
    }

    const alts = parseAlternatives(d.alternatives);
    if (alts.length > 0) {
      lines.push("### Alternatives Considered\n");
      alts.forEach((a) => lines.push(`- ${a}`));
      lines.push("");
    }

    lines.push("---\n");
  }

  const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `decisions-${new Date().toISOString().slice(0, 10)}.md`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

function DecisionsSheetBody({ decisions }: { decisions: Decision[] }) {
  if (decisions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <p className="text-sm">No decisions recorded yet</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 pb-4">
      {decisions.map((d) => {
        const cat = CATEGORY_META[d.category] ?? { emoji: "❓", color: "text-zinc-400" };
        const impactStyle = IMPACT_STYLE[d.impact ?? "low"] ?? IMPACT_STYLE.low;

        return (
          <div
            key={d.id}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-2.5"
          >
            {/* Header: category + impact */}
            <div className="flex items-center gap-2">
              <span className="text-base">{cat.emoji}</span>
              <span className={cn("text-xs uppercase tracking-wider font-medium", cat.color)}>
                {d.category}
              </span>
              {d.impact && (
                <span className={cn("ml-auto text-xs uppercase tracking-wider font-medium border rounded-full px-2 py-0.5", impactStyle)}>
                  {d.impact}
                </span>
              )}
            </div>

            {/* Question */}
            <p className="text-sm font-medium text-foreground leading-snug">{d.question}</p>

            {/* Decision */}
            <div className="rounded-md bg-white/[0.03] border border-white/[0.06] px-3 py-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Decision</p>
              <p className="text-sm text-foreground">{d.decision}</p>
            </div>

            {/* Rationale */}
            {d.rationale && (
              <p className="text-xs text-muted-foreground leading-relaxed">{d.rationale}</p>
            )}

            {/* Alternatives */}
            {(() => {
              const alts = parseAlternatives(d.alternatives);
              return alts.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Alternatives considered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {alts.map((alt, i) => (
                      <span
                        key={i}
                        className="text-xs text-muted-foreground bg-white/[0.04] border border-white/[0.06] rounded-full px-2.5 py-0.5"
                      >
                        {alt}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Footer */}
            <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
              {d.madeBy && <span>{d.madeBy}</span>}
              {d.madeBy && d.createdAt && <span>·</span>}
              {d.createdAt && (
                <span>{new Date(d.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
